import { z } from 'zod'
import { applyMask } from '~/packages/general/detailedInformation/informationMasks'
import { formatDate } from '~/prix/types/date'
import { formatAsMoney } from '~/prix/utils/types/number'
import type { RuralReportTemplateProps } from '../../report/ruralReportTemplateTitle'
import { creditProgramType } from '../../creditProgram/creditProgramObject'
import { creditRequestStatus } from '../creditRequestObject'

export const creditRequestReportTypeSchema = z.enum(['payment', 'credit'])

export type CreditRequestReportType = z.infer<typeof creditRequestReportTypeSchema>

export interface CreditRequestReportData {
  id: string
  code: number
  requestDate?: Date | string | null
  status?: string | null
  totalMovementValue: number
  estimatedBonusValue: number
  creditProgramName?: string | null
  creditProgramDescription?: string | null
  creditProgramActiveYear?: number | null
  creditProgramGeneralRules?: string | null
  creditProgramType?: string | null
  requesterFullName?: string | null
  requesterSocialName?: string | null
  requesterCpf?: string | null
  requesterFullAddress?: string | null
  residenceCode?: number | string | null
  residenceTitle?: string | null
  residenceFullAddress?: string | null
  bankNumber?: string | null
  bankName?: string | null
  bankBranchName?: string | null
  bankBranchNumber?: string | null
  bankAccountNumber?: string | null
  bankAccountHolderName?: string | null
  pixKey?: string | null
}

export interface CreditRequestReportProps extends RuralReportTemplateProps {
  type: CreditRequestReportType
  data?: CreditRequestReportData | null
}

export const creditRequestReportColor = '#00A859'

export const creditRequestReportTitleMap: Record<CreditRequestReportType, string> = {
  payment: 'TERMO DE AUTORIZAÇÃO PARA PAGAMENTO DE INCENTIVO/BENEFÍCIO',
  credit: 'REQUERIMENTO DE SOLICITAÇÃO DE INCENTIVO RURAL',
}

export const creditRequestReportTemplateTitleMap: Record<CreditRequestReportType, string> = {
  payment: 'TERMO DE AUTORIZAÇÃO',
  credit: 'REQUERIMENTO DE SOLICITAÇÃO',
}

export function buildCreditRequestReportFileBaseName(type: CreditRequestReportType) {
  return type === 'payment' ? 'credit-request-payment-report' : 'credit-request-report'
}

export function formatCreditRequestReportDate(value?: Date | string | null) {
  if (!value) {
    return 'Não informado'
  }

  const parsedValue = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(parsedValue.getTime())) {
    return String(value)
  }

  return formatDate(parsedValue)
}

export function formatCreditRequestText(value?: string | null) {
  return value?.trim() || 'Não informado'
}

export function formatCreditRequestMoney(value?: number | string | null) {
  const numericValue = Number(value ?? 0)

  if (!Number.isFinite(numericValue)) {
    return formatAsMoney(0)
  }

  return formatAsMoney(numericValue)
}

export function formatCreditRequestCpf(value?: string | null) {
  return value ? applyMask(value, 'cpf') : 'Não informado'
}

export function buildCreditRequestStatusLabel(value?: string | null) {
  if (!value) {
    return 'Não informado'
  }

  return (
    creditRequestStatus.keysAndLabels?.[
      value as keyof NonNullable<typeof creditRequestStatus.keysAndLabels>
    ] ?? value
  )
}

export function buildCreditProgramTypeLabel(value?: string | null) {
  if (!value) {
    return 'Não informado'
  }

  return (
    creditProgramType.keysAndLabels?.[
      value as keyof NonNullable<typeof creditProgramType.keysAndLabels>
    ] ?? value
  )
}

export function buildCreditRequestRequesterName(data?: CreditRequestReportData | null) {
  const requesterFullName = data?.requesterFullName?.trim()
  const requesterSocialName = data?.requesterSocialName?.trim()

  if (requesterSocialName) {
    return requesterSocialName
  }

  if (!requesterSocialName && requesterFullName) {
    return requesterFullName
  }

  return requesterSocialName || requesterFullName || 'Não informado'
}

export function buildCreditRequestResidenceLabel(data?: CreditRequestReportData | null) {
  const residenceParts = [
    data?.residenceTitle?.trim() || null,
    data?.residenceCode !== undefined && data?.residenceCode !== null
      ? `Código ${data.residenceCode}`
      : null,
  ].filter(Boolean)

  if (residenceParts.length > 0) {
    return residenceParts.join(' - ')
  }

  return data?.residenceFullAddress?.trim() || data?.requesterFullAddress?.trim() || 'Não informado'
}
